import PropTypes from 'prop-types';
import { useState } from 'react';
import {
    Box,
    Typography,
    Collapse,
    Divider,
    List,
    Tooltip,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssessmentIcon from '@mui/icons-material/Assessment';
import NavItem from '../NavItem';
import NavCollapse from '../NavCollapse';

/* ─────────────────────────────────────────────
   Group icon resolver
───────────────────────────────────────────── */
const getGroupIcon = (title) => {
    const props = { sx: { fontSize: 18, color: '#fff' } };
    switch (title?.toLowerCase()) {
        case 'master': return <SettingsApplicationsIcon {...props} />;
        case 'manage tasks': return <AssignmentTurnedInIcon   {...props} />;
        case 'dashboard': return <DashboardIcon            {...props} />;
        case 'report': return <AssessmentIcon           {...props} />;
        case 'payment history': return <ReceiptLongIcon          {...props} />;
        default: return <HelpOutlineIcon          {...props} />;
    }
};

/* ─────────────────────────────────────────────
   Component
───────────────────────────────────────────── */
const NavGroup = ({ item, isCollapsed }) => {
    const [open, setOpen] = useState(false);

    const toggle = () => setOpen((p) => !p);

    const children = item.children?.map((menu) => {
        switch (menu.type) {
            case 'collapse':
                return <NavCollapse key={menu.id} menu={menu} level={1} isCollapsed={isCollapsed} />;
            case 'item':
                return <NavItem key={menu.id} item={menu} level={1} isCollapsed={isCollapsed} />;
            default:
                return (
                    <Typography key={menu.id} variant="caption" color="error" align="center" sx={{ display: 'block', px: 2, py: 0.5 }}>
                        Menu error
                    </Typography>
                );
        }
    });

    /* ── Collapsed sidebar — icon-only button ── */
    if (isCollapsed) {
        return (
            <>
                <Tooltip title={item.title} placement="right" arrow>
                    <Box
                        onClick={toggle}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: 44,
                            cursor: 'pointer',
                            borderRadius: '6px',
                            mx: 'auto',
                            transition: 'background 0.15s',
                            '&:hover': {
                                backgroundColor: 'rgba(255,255,255,0.10)',
                            },
                        }}
                    >
                        {getGroupIcon(item.title)}
                    </Box>
                </Tooltip>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.10)', my: 0.5 }} />
            </>
        );
    }

    /* ── Expanded sidebar ── */
    return (
        <>
            {/* Group header row */}
            <Box
                onClick={toggle}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 2,
                    mt: 1,
                    cursor: 'pointer',
                    borderRadius: '6px',
                    transition: 'background 0.15s',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
                    userSelect: 'none',
                }}
            >
                {/* Icon */}
                <Box
                    sx={{
                        width: 28, height: 28, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        borderRadius: '6px',
                        backgroundColor: open ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.07)',
                        transition: 'background 0.15s',
                    }}
                >
                    {getGroupIcon(item.title)}
                </Box>

                {/* Title */}
                <Typography
                    sx={{
                        flex: 1,
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        color: '#fff',
                        letterSpacing: '0.03em',
                        textTransform: 'uppercase',
                        lineHeight: 1,
                        transition: 'color 0.15s',
                    }}
                >
                    {item.title}
                </Typography>

                {/* Expand / collapse chevron */}
                <Box
                    sx={{
                        width: 18, height: 18, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff',
                        transition: 'transform 0.2s, color 0.15s',
                        transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                    }}
                >
                    <ExpandLessIcon sx={{ fontSize: 16 }} />
                </Box>
            </Box>

            {/* Children */}
            <Collapse in={open} timeout="auto" unmountOnExit>
                <List
                    disablePadding
                    sx={{
                        pt: 0.5, pb: 0.5,
                        '& .MuiListItemText-primary': { color: 'rgba(255,255,255,0.85)' },
                    }}
                >
                    {children}
                </List>
            </Collapse>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.10)', mt: 0.5, mb: 1, mx: -5 }} />
        </>
    );
};

NavGroup.propTypes = {
    item: PropTypes.object.isRequired,
    isCollapsed: PropTypes.bool,
};

export default NavGroup;